import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, tableSheet } from "./excel";

const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const percent = (value) => (value === null || value === undefined ? "-" : `${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`);

const VALUATION_TYPES = [
  { value: "basic", label: "Basic" },
  { value: "cost", label: "Cost / Purchase Price" },
  { value: "selling", label: "Selling Price" },
];

const ClosingStockReport = () => {
  const [valuationType, setValuationType] = useState("basic");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (type) => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/reports/closing-stock", { valuationType: type });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load closing stock report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(valuationType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuationType]);

  const showTax = valuationType !== "basic";
  const rateLabel = valuationType === "selling" ? "Selling Price" : valuationType === "cost" ? "Cost / Purchase Rate" : "Rate";

  const totalQty = rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const totalValue = rows.reduce((sum, r) => sum + (Number(r.totalNetValue) || 0), 0);

  const exportExcel = () => {
    const valuation = VALUATION_TYPES.find((t) => t.value === valuationType).label;
    const columns = [
      { key: "skuCode", label: "SKU" },
      { key: "productName", label: "Product" },
      { key: "category", label: "Category" },
      { key: "hsnCode", label: "HSN Code" },
      { key: "quantity", label: "Qty", format: "qty" },
      { key: "rate", label: rateLabel, format: "money", total: false },
      ...(showTax
        ? [
            { key: "gstPercent", label: "GST %" },
            { key: "rateInclTax", label: "Rate Incl. Tax", format: "money", total: false },
          ]
        : []),
      { key: "totalNetValue", label: "Total Net Value", format: "money" },
      { key: "mrp", label: "MRP", format: "money", total: false },
    ];
    download(fileName("Closing Stock", valuation), [tableSheet("Closing Stock", `Valuation: ${valuation}`, columns, rows)]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading closing stock...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Current on-hand quantity per product, valued at the selected price basis.
      </div>

      <div className="filters-bar">
        <select value={valuationType} onChange={(e) => setValuationType(e.target.value)} aria-label="Valuation type">
          {VALUATION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button className="primary" onClick={() => load(valuationType)} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || rows.length === 0} />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product</th>
              <th>Category</th>
              <th>HSN Code</th>
              <th>Qty</th>
              <th>{rateLabel}</th>
              {showTax ? <th>GST %</th> : null}
              {showTax ? <th>Rate Incl. Tax</th> : null}
              <th>Total Net Value</th>
              <th>MRP</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={showTax ? 10 : 8}>
                  <div className="empty-state">
                    <span className="empty-state-title">No stock on hand</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.productId}>
                  <td>{row.skuCode}</td>
                  <td>{row.productName}</td>
                  <td>{row.category || "-"}</td>
                  <td>{row.hsnCode || "-"}</td>
                  <td>{qty(row.quantity)}</td>
                  <td>{money(row.rate)}</td>
                  {showTax ? <td>{percent(row.gstPercent)}</td> : null}
                  {showTax ? <td>{money(row.rateInclTax)}</td> : null}
                  <td>{money(row.totalNetValue)}</td>
                  <td>{money(row.mrp)}</td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                <td style={{ fontWeight: 700 }}>Total</td>
                <td />
                <td />
                <td />
                <td style={{ fontWeight: 700 }}>{qty(totalQty)}</td>
                <td />
                {showTax ? <td /> : null}
                {showTax ? <td /> : null}
                <td style={{ fontWeight: 700 }}>{money(totalValue)}</td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default ClosingStockReport;
